import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import './OrdersManager.css';

const OrdersManager = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/orders');
      setOrders(response.data);
    } catch (err) {
      setError('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (orderId, status) => {
    try {
      await api.put(`/admin/orders/${orderId}`, { status });
      fetchOrders();
      if (selectedOrder?.id === orderId) {
        setSelectedOrder({ ...selectedOrder, status });
      }
    } catch (err) {
      alert('Failed to update order status');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return '#ff9800';
      case 'paid':
        return '#2196F3';
      case 'processing':
        return '#9C27B0';
      case 'shipped':
        return '#4CAF50';
      case 'delivered':
        return '#4CAF50';
      case 'cancelled':
        return '#f44336';
      default:
        return '#666';
    }
  };

  if (loading) {
    return <div className="loading">Loading orders...</div>;
  }

  return (
    <div className="orders-manager">
      <h1>Manage Orders</h1>

      {error && <div className="error-message">{error}</div>}

      {orders.length === 0 ? (
        <div className="empty-state">
          <h2>No orders yet</h2>
          <p>Orders will appear here when customers make purchases</p>
        </div>
      ) : (
        <div className="orders-content">
          <div className="orders-list">
            {orders.map((order) => (
              <div
                key={order.id}
                className={`order-card card ${selectedOrder?.id === order.id ? 'selected' : ''}`}
                onClick={() => setSelectedOrder(order)}
              >
                <div className="order-header">
                  <div>
                    <h3>{order.order_number}</h3>
                    <p className="order-date">
                      {new Date(order.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div>
                    <span
                      className="order-status"
                      style={{ backgroundColor: getStatusColor(order.status) }}
                    >
                      {order.status}
                    </span>
                  </div>
                </div>
                <div className="order-summary">
                  <p>{order.customer_name}</p>
                  <p className="order-total">${order.total_amount.toFixed(2)}</p>
                </div>
              </div>
            ))}
          </div>

          {selectedOrder && (
            <div className="order-details card">
              <h2>Order Details</h2>

              <div className="detail-section">
                <h3>Order Information</h3>
                <div className="detail-row">
                  <span>Order Number:</span>
                  <span>{selectedOrder.order_number}</span>
                </div>
                <div className="detail-row">
                  <span>Date:</span>
                  <span>{new Date(selectedOrder.created_at).toLocaleString()}</span>
                </div>
                <div className="detail-row">
                  <span>Status:</span>
                  <select
                    value={selectedOrder.status}
                    onChange={(e) => updateOrderStatus(selectedOrder.id, e.target.value)}
                    className="status-select"
                  >
                    <option value="pending">Pending</option>
                    <option value="paid">Paid</option>
                    <option value="processing">Processing</option>
                    <option value="shipped">Shipped</option>
                    <option value="delivered">Delivered</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
              </div>

              <div className="detail-section">
                <h3>Customer Information</h3>
                <div className="detail-row">
                  <span>Name:</span>
                  <span>{selectedOrder.customer_name}</span>
                </div>
                <div className="detail-row">
                  <span>Email:</span>
                  <span>{selectedOrder.customer_email}</span>
                </div>
                {selectedOrder.customer_phone && (
                  <div className="detail-row">
                    <span>Phone:</span>
                    <span>{selectedOrder.customer_phone}</span>
                  </div>
                )}
                <div className="detail-row">
                  <span>Shipping Address:</span>
                  <span className="address">{selectedOrder.shipping_address}</span>
                </div>
              </div>

              <div className="detail-section">
                <h3>Items</h3>
                {selectedOrder.items.map((item, index) => (
                  <div key={index} className="order-item-detail">
                    <div>
                      <p className="item-name">{item.item_name}</p>
                      <p className="item-quantity">Quantity: {item.quantity}</p>
                    </div>
                    <p className="item-price">
                      ${(item.item_price * item.quantity).toFixed(2)}
                    </p>
                  </div>
                ))}
                <div className="order-total-row">
                  <span>Total:</span>
                  <span>${selectedOrder.total_amount.toFixed(2)}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default OrdersManager;
